import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { Callout, CallToAction, ColorKeys, PageLinkType, TextOverrides } from '../../../../../../types/working-model';
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import Modal from '../../../../../general-ui/modal/modal';
import Icon, { ICONS, COLORS } from '../../../../../general-ui/icon';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import Controls from '../../../session/editor/moduleControls/controls';
import { getStylingOverrides } from '../../../../../live-event/utils';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import CallToActionForm from '../../../../../general-ui/call-to-action/call-to-action';
import { Button } from '../../../../../general-ui/button/button';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { BasicLangingModuleProps } from '../editor-main';
import '../../../../../../scss/live-event/base/modules/call-out.scss';
import './callout.scss';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { Tooltip } from '../../../../../general-ui/tooltip/tooltip';
import { EUploadOptions, ImageUploader, ImageUploadOptions } from '../../../../../general-ui/image-uploader/image-uploader';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';

const EditorCallout: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);

	const currentPostRegisterPage = useTypedSelector(state => state.CreateEventReducer.currentPostRegisterPage);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const dispatch = useDispatch();
	const [callToActionOpen, setCallToActionOpen] = useState(false);
	const [workingCTA, setWorkingCTA] = useState<CallToAction | null>(page_module?.content?.callToAction);
	const [imageModalOpen, setImageModalOpen] = useState<ImageUploadOptions>(null);

	const content: Callout = page_module?.content;

	const layout_type = content?.layout_type || "standard";
	const styling_overrides = page_module.styling_overrides;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const originalImage = content?.originalImage;
	const image = content?.image;

	const callOutImg = image ? { backgroundImage: `url(${image})` } : {};

	function setModuleTitle(text: string) {
		dispatch(
			updateHomepageModule(
				{
					...page_module,
					content: {
						...content,
						title: updateTranslateKey({
							translateString: content?.title,
							input: text,
							language,
							baseLanguage
						}),
					},
					is_edited: true,
				}
			)
		);
	}

	function handleDescriptionChange(text: string) {
		dispatch(
			updateHomepageModule(
				{
					...page_module,
					content: {
						...content,
						description: updateTranslateKey({
							translateString: content?.description,
							input: text,
							baseLanguage,
							language
						})
					},
					is_edited: true,
				}
			)
		);
	}

	function setCallToActionButton() {
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...content,
					callToAction: workingCTA
				}
			})
		);
		setCallToActionOpen(false);
		setWorkingCTA(null);
	}

	function cancelEditButton() {
		setCallToActionOpen(false);
		setWorkingCTA(null);
	}

	function editButton() {
		setCallToActionOpen(true);
	}

	const saveImage = useCallback((image: { original: string, edited: string; }) => {
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...page_module.content,
					originalImage: image.original,
					image: image.edited,
				}
			})
		);
	}, [dispatch, page_module]);

	function handleDescriptionOverrideChange(overrides: TextOverrides) {
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				description: overrides
			}
		};
		dispatch(
			updateHomepageModule({
				...page_module,
				styling_overrides: updatedOverrides
			})
		);
	}
	function handleTitleOverrideChange(overrides: TextOverrides) {
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				title: overrides
			}
		};
		dispatch(
			updateHomepageModule({
				...page_module,
				styling_overrides: updatedOverrides
			})
		);
	}
	const { description, title } = getTextValue(content, language);

	const isLinkedToRegistration = content?.callToAction?.content?.page === PageLinkType.registration;
	const callToActionButton = useMemo(() => {
		return (
			<Button
				classButton={classNames(
					'primary',
				)}
				template={template}
			>
				{t(`${namespace}:callout.${page_module.id}.callToAction`, t(`${namespace}:callout.callToAction`, content?.callToAction?.button_name[language] as string || content?.callToAction?.button_name.base))}
			</Button>
		);
	}, [content?.callToAction?.button_name, language, template]);

	const titleDefaultColor = layout_type === "ltr"
		? ColorKeys.bodyTextColor
		: ColorKeys.headingTextColor;
	const descriptionDefaultColor = template === TemplateClassNames.O2
		? ColorKeys.bodyTextColor
		: ColorKeys.secondaryAccentColor;

	return page_module.is_on && workingEvent ? (
		<section
			ref={ref}
			className={classNames(
				"editor-wrapper call-out",
				`call-out-${layout_type}`,
				stylingOverrides.backgroundColor,
				template
			)}
		>
			<Controls
				module={page_module}
				editableContent={{
					jsx: <CallToActionForm
						workingCTA={workingCTA || page_module?.content?.callToAction}
						onWorkingCTAChange={setWorkingCTA}
						language={language}
						baseLanguage={baseLanguage}
					/>,
					onDone: setCallToActionButton,
				}}
				display={{
					editColors: true,
					editLayout: true,
					changeSectionPosition: true,
				}}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={stylingOverrides.backgroundImg}>
				<div className="call-out-inner">
					<EditableTextSlim
						tagType="p"
						className="upper-call-out-title"
						elementName={EContentColorOverrideNames.description}
						colorOverride={styling_overrides?.content?.description?.color}
						defaultColor={descriptionDefaultColor}
						value={t(`${namespace}:callout.${page_module.id}.description`, description)}
						onChange={handleDescriptionChange}
						onOverrideChange={handleDescriptionOverrideChange}
					/>
					<EditableTextSlim
						className="call-out-title"
						tagType="h2"
						elementName={EContentColorOverrideNames.title}
						colorOverride={styling_overrides?.content?.title?.color}
						defaultColor={titleDefaultColor}
						value={t(`${namespace}:callout.${page_module.id}.title`, title)}
						onChange={setModuleTitle}
						onOverrideChange={handleTitleOverrideChange}
					/>

					<div className='call-to-action-wrapper'>
						{
							(currentPostRegisterPage && isLinkedToRegistration) ? (
								<Tooltip tooltip="This button will not display when linked to registration">
									{callToActionButton}
								</Tooltip>
							) : callToActionButton
						}
						<button className="round edit-button" onClick={editButton}><Icon name={ICONS.EDIT} size={13} color={COLORS.WHITE} /></button>
					</div>

				</div>
				<div className="call-out-image" style={callOutImg}>
					<div className='edit-button-container'>
						{image ? (
							<button className="round" onClick={() => setImageModalOpen(EUploadOptions.edit)}><Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} /></button>
						) : null}
						<button className="round replace-image" onClick={() => setImageModalOpen(EUploadOptions.upload)}>
							<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
						</button>
					</div>
				</div>

			</div>

			<Modal
				cancellable={true}
				closeable={false}
				open={callToActionOpen}
				onRequestClose={cancelEditButton}
				title={"Edit button"}
				footer={(
					<>
						<button onClick={cancelEditButton}>Cancel</button>
						<button onClick={setCallToActionButton} className="primary">Done</button>
					</>
				)}
			>
				<CallToActionForm
					workingCTA={workingCTA || page_module?.content?.callToAction}
					onWorkingCTAChange={setWorkingCTA}
					language={language}
					baseLanguage={baseLanguage}
				/>
			</Modal>

			<ImageUploader
				originalImage={originalImage}
				onSave={saveImage}
				onClose={() => setImageModalOpen(null)}
				open={imageModalOpen}
			/>
		</section>
	) : null;
});

EditorCallout.displayName = 'EditorCallout';

export default EditorCallout;
